import { Component, ErrorInfo, PropsWithChildren, ReactElement, useCallback, useMemo } from 'react';
import { Alert } from '@mui/material';
import { JSONSchema7 } from 'json-schema';
import { SchemaForm, type SchemaFormOptions, type SchemaFormSelectOptionContext, type SchemaFormWidgets, type SchemaFormValidationError } from 'formhell';
import { getByPointer } from 'json-pointer-relational';
import { ErrorsByPointer } from '../../utils/jsonSchemaValidator/errorMapping';
import { JSON_POINTER_FORMAT, X_UI_FORMAT } from '../../utils/schemaExtensions';
import { normalizeSchemaForFormHell } from './schemaNormalization';
import { SchemaFormContextProvider, SchemaFormContextValue } from './SchemaFormContext';
import { resolveAppWidget } from './widgets';
import { collectWidgetPointers } from './widgetPointers';
import './AppSchemaForm.css';

export type SchemaFormData = Record<string, any>;

export interface AppSchemaFormProps {
    /** Must be referentially stable (memoize it): FormHell re-initializes for every new schema object. */
    schema: JSONSchema7;
    values: SchemaFormData | undefined;
    onChange: (data: SchemaFormData) => void;
    /** Errors from outside the form (e.g. the server rejecting a save), shown under the field they point at. */
    extraErrorsByPointer?: ErrorsByPointer;
    imagePreviewMap?: Record<string, string>;
    readOnly?: boolean;
    widgets?: SchemaFormWidgets;
}

type BoundaryState = { error?: Error };

class SchemaFormErrorBoundary extends Component<PropsWithChildren<{ resetKey: unknown }>, BoundaryState> {
    state: BoundaryState = {};

    static getDerivedStateFromError(error: Error): BoundaryState {
        return { error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('Schema form failed to render', error, info.componentStack);
    }

    componentDidUpdate(prevProps: { resetKey: unknown }) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
            this.setState({ error: undefined });
        }
    }

    render(): ReactElement {
        if (this.state.error) {
            return (
                <Alert severity="error">
                    Unable to render this form: {this.state.error.message}
                </Alert>
            );
        }
        return <>{this.props.children}</>;
    }
}

const FORM_OPTIONS: SchemaFormOptions = {
    defaults: 'required-only',
    showFieldValidationMessages: true,
    showFormValidationMessages: false,
    virtualization: { enabled: true, objects: { enabled: true } },
};

export const AppSchemaForm = ({
    schema,
    values,
    onChange,
    extraErrorsByPointer,
    imagePreviewMap,
    readOnly = false,
    widgets: extraWidgets,
}: AppSchemaFormProps) => {
    const normalizedSchema = useMemo(() => normalizeSchemaForFormHell(schema), [schema]);

    const hasValues = values !== undefined;
    const handleChange = useCallback((next: unknown, _errors: SchemaFormValidationError[], fieldPointer?: string, prev?: unknown) => {
        if (fieldPointer === '' && prev === undefined && hasValues) {
            return;
        }
        onChange((next ?? {}) as SchemaFormData);
    }, [onChange, hasValues]);

    const widgets = useMemo<SchemaFormWidgets>(() => ({
        ...collectWidgetPointers(normalizedSchema, resolveAppWidget),
        ...(extraWidgets || {}),
    }), [normalizedSchema, extraWidgets]);

    // json-pointer selects show the value each option points to in the form data
    const selectOptionFormatter = useCallback(({ label, value, schema: optionSchema }: SchemaFormSelectOptionContext) => {
        if ((optionSchema as Record<string, unknown>)[X_UI_FORMAT] !== JSON_POINTER_FORMAT) {
            return label;
        }
        try {
            return String(getByPointer(String(value), values ?? {}) ?? label);
        } catch {
            return label;
        }
    }, [values]);
    // Memoized: a new options object on every render would re-render the whole form
    const options = useMemo<SchemaFormOptions>(() => ({ ...FORM_OPTIONS, selectOptionFormatter }), [selectOptionFormatter]);

    const context = useMemo<SchemaFormContextValue>(() => ({
        imagePreviewMap: imagePreviewMap ?? {},
        readOnly,
    }), [imagePreviewMap, readOnly]);

    return (
        <SchemaFormContextProvider value={context}>
            <div className="app-schema-form">
                <SchemaFormErrorBoundary resetKey={normalizedSchema}>
                    <fieldset className="app-sf-fieldset" disabled={readOnly}>
                        <SchemaForm
                            schema={normalizedSchema}
                            data={values}
                            widgets={widgets}
                            externalErrors={extraErrorsByPointer}
                            options={options}
                            onChange={handleChange}
                        />
                    </fieldset>
                </SchemaFormErrorBoundary>
            </div>
        </SchemaFormContextProvider>
    );
};
